import type { ReportTableCell, ReportTableProps } from '@/components';
import type {
  ReportStandardFundForPublicModel,
  ReportStandardFundsForPublicModel,
  projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { isBefore2025 } from '@/features/project/_utils/project';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { calcAdministrativeExpensesRate } from '@/features/project/projects/detail/income-and-expenditure/_utils/fund-income-and-expenditure-table';
import { t } from '@/i18n';
import { formatAmountForPdf } from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../pdf/pdf-utils';
import { standardFundReportForPublicValueConverter } from './public-converter';

const getPublicReportTargetYear = (fiscalYear: number) => {
  return fiscalYear - 1;
};

const headerTextStyleForPublic: CSSProperties = {
  textAlign: 'center',
  fontSize: 6,
};
const rowTextStyleForPublic: CSSProperties = {
  fontSize: 6,
};
const amountTextStyleForPublic: CSSProperties = {
  ...rowTextStyleForPublic,
  textAlign: 'right',
};

const columnRatios = {
  fundSheetNumber: 1,
  ministry: 2,
  additionalOrganizationInCharge: 2,
  fundBusinessName: 3,
  fundName: 3,
  fundOwner: 3,
  operationForm: 2,
  businessForm: 2,
  beginnningOfTheFundBalance: 2.5,
  totalIncome: 2.5,
  totalExpenditure: 5,
  amountReturnedToTheNationalTreasury: 2.5,
  amountReturnedToTheOrganization: 2.5,
  endingOfTheFundBalance: 2.5,
};

const getPdfText = (
  key: Exclude<
    keyof z.infer<typeof ReportStandardFundForPublicModel>,
    'administrativeExpenseRate'
  >,
  value: string | number | string[] | null,
) => {
  if (Array.isArray(value)) {
    switch (key) {
      case 'operationForms':
        return value.map((d) => OperationFormLabelModifier[d]).join(', ');
      case 'businessForms':
        return value.map((d) => BusinessFormLabelModifier[d]).join(', ');
      default:
        return value.join(', ');
    }
  }
  return value === null ? '' : value;
};

export const getStandardFundReportForPublicHeader = (
  fiscalYear: number,
): ReportTableProps['header'] => {
  const reportTargetYear = getPublicReportTargetYear(fiscalYear);
  const previousReportTargetYear = reportTargetYear - 1;

  return [
    {
      type: 'text',
      value: t('report-standard-fund-header-fund-sheet-number'),
      ratio: columnRatios.fundSheetNumber,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('ministry'),
      ratio: columnRatios.ministry,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('additional-organization-in-charge'),
      ratio: columnRatios.additionalOrganizationInCharge,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('fund-business-name'),
      ratio: columnRatios.fundBusinessName,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('fund-name'),
      ratio: columnRatios.fundName,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('fund-owner'),
      ratio: columnRatios.fundOwner,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('operation-form'),
      ratio: columnRatios.operationForm,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('business-form'),
      ratio: columnRatios.businessForm,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t(
        'report-standard-fund-header-public-beginning-of-the-fund-balance',
        { year: previousReportTargetYear },
      ),
      ratio: columnRatios.beginnningOfTheFundBalance,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t('report-standard-fund-header-public-total-income', {
        year: reportTargetYear,
      }),
      ratio: columnRatios.totalIncome,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'multiple',
      cell: {
        type: 'subset',
        cellColRatios: [1, 2],
        cellRowRatios: [1, 3],
        isContainedCellFullHeight: true,
        containedCell: {
          type: 'text',
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          value: t('report-standard-fund-header-public-total-expenses', {
            year: reportTargetYear,
          }),
          textStyle: headerTextStyleForPublic,
        },
        includedCell: {
          type: 'multiple',
          cell: {
            flexCells: [
              {
                ...centerTextStyle,
                value: t(
                  'report-standard-fund-public-header-administrative-expense',
                ),
                textStyle: headerTextStyleForPublic,
              },
              {
                ...centerTextStyle,
                value: t(
                  'report-standard-fund-public-header-administrative-expense-rate',
                ),
                textStyle: headerTextStyleForPublic,
              },
            ],
          },
        },
      },
      ratio: columnRatios.totalExpenditure,
    },
    {
      type: 'text',
      value: t(
        'report-standard-fund-public-header-amount-returned-to-the-national-treasury',
        { year: reportTargetYear },
      ),
      ratio: columnRatios.amountReturnedToTheNationalTreasury,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: isBefore2025({ fiscalYear })
        ? t(
            'report-standard-fund-public-header-amount-returned-to-the-organization-before-2025',
            { year: reportTargetYear },
          )
        : t(
            'report-standard-fund-public-header-amount-returned-to-the-organization',
            { year: reportTargetYear },
          ),
      ratio: columnRatios.amountReturnedToTheOrganization,
      textStyle: headerTextStyleForPublic,
    },
    {
      type: 'text',
      value: t(
        'report-standard-fund-public-header-ending-of-the-fund-balance',
        {
          year: reportTargetYear,
        },
      ),
      ratio: columnRatios.endingOfTheFundBalance,
      textStyle: headerTextStyleForPublic,
    },
  ];
};

const getStandardFundReportForPublicRow = ({
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportStandardFundForPublicModel>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableCell[] => {
  const values = standardFundReportForPublicValueConverter({
    report: data,
    type: 'pdf',
    isDebugMode,
  });
  return [
    {
      type: 'text',
      value: getPdfText(
        'fundSheetNumber',
        `${data.fundSheetNumber.toString()}${
          data.fundSheetBranchNumber ? `-${data.fundSheetBranchNumber}` : ''
        }`,
      ),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText('ministryName', data.ministryName),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText(
        'additionalJurisdictionalMinistries',

        data.additionalJurisdictionalMinistries,
      ),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText('fundBusinessName', data.fundBusinessName),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'link',
      href: getProjectDetailPageUrl({
        isPS: projectDetailUrlType === 'public',
        projectId: data.id,
      }),
      value: String(getPdfText('projectName', data.projectName)),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText('fundOwner', data.fundOwner),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText('operationForms', data.operationForms),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: getPdfText('businessForms', data.businessForms),
      textStyle: rowTextStyleForPublic,
    },
    {
      type: 'text',
      value: formatAmountForPdf(data.beginningOfTheFundBalance, {
        rawValue: data.rawBeginningOfTheFundBalance,
      }),
      textStyle: amountTextStyleForPublic,
    },
    {
      type: 'text',
      value: values.totalIncome,
      textStyle: amountTextStyleForPublic,
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        cellRatios: [1, 2],
        flexCells: [
          {
            ...centerTextStyle,
            value: values.totalExpenses,
            textStyle: amountTextStyleForPublic,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              flexCells: [
                {
                  ...centerTextStyle,
                  value: formatAmountForPdf(data.administrativeExpense, {
                    rawValue: data.rawAdministrativeExpense,
                  }),
                  textStyle: amountTextStyleForPublic,
                },
                {
                  ...centerTextStyle,
                  // TODO: https://linear.app/wise-vine/issue/PRZ-3238/improve-基金の乖離率の計算ロジックをbeに寄せる
                  value: calcAdministrativeExpensesRate({
                    administrativeExpense: data.administrativeExpense ?? 0,
                    sum: data.totalExpenses,
                  }),
                  textStyle: amountTextStyleForPublic,
                },
              ],
            },
          },
        ],
      },
    },
    {
      type: 'text',
      value: formatAmountForPdf(data.amountReturnedToTheNationalTreasury, {
        rawValue: data.rawAmountReturnedToTheNationalTreasury,
      }),
      textStyle: amountTextStyleForPublic,
    },
    {
      type: 'text',
      value: formatAmountForPdf(data.amountReturnedToTheOrganization, {
        rawValue: data.rawAmountReturnedToTheOrganization,
      }),
      textStyle: amountTextStyleForPublic,
    },
    {
      type: 'text',
      value: values.endingOfTheFundBalance,
      textStyle: amountTextStyleForPublic,
    },
  ];
};

export const getStandardFundReportForPublicRowData = ({
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: Array<z.infer<typeof ReportStandardFundForPublicModel>>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableProps['data'] => {
  return data.map((d) => ({
    row: getStandardFundReportForPublicRow({
      data: d,
      isDebugMode,
      projectDetailUrlType,
    }),
  }));
};

export const getStandardFundReportForPublicFooter = (
  summary: z.infer<typeof ReportStandardFundsForPublicModel.shape.summaryData>,
): ReportTableProps['footer'] => {
  return [
    {
      row: [
        {
          type: 'text',
          value: t('total'),
          colSpan: 8,
          align: 'center',
          textStyle: rowTextStyleForPublic,
        },
        {
          type: 'text',
          value: formatAmountForPdf(summary.beginningOfTheFundBalanceSummary),
          textStyle: amountTextStyleForPublic,
        },
        {
          type: 'text',
          value: formatAmountForPdf(summary.totalIncomeSummary),
          textStyle: amountTextStyleForPublic,
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            cellRatios: [1, 2],
            flexCells: [
              {
                type: 'text',
                value: formatAmountForPdf(summary.totalExpensesSummary),
                textStyle: amountTextStyleForPublic,
              },
              {
                type: 'multiple',
                cell: {
                  type: 'flex',
                  flexCells: [
                    {
                      type: 'text',
                      value: formatAmountForPdf(
                        summary.administrativeExpenseSummary,
                      ),
                      textStyle: amountTextStyleForPublic,
                    },
                    {
                      type: 'text',
                      value: '',
                      textStyle: amountTextStyleForPublic,
                    },
                  ],
                },
              },
            ],
          },
        },
        {
          type: 'text',
          value: formatAmountForPdf(
            summary.amountReturnedToTheNationalTreasurySummary,
          ),
          textStyle: amountTextStyleForPublic,
        },
        {
          type: 'text',
          value: formatAmountForPdf(
            summary.amountReturnedToTheOrganizationSummary,
          ),
          textStyle: amountTextStyleForPublic,
        },
        {
          type: 'text',
          value: formatAmountForPdf(summary.endingOfTheFundBalanceSummary),
          textStyle: amountTextStyleForPublic,
        },
      ],
    },
  ];
};
